import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { DollarSign, Edit, Plus, RefreshCw, Trash2 } from "lucide-react";
import {
  useGetFxRates,
  useCreateFxRate,
  useUpdateFxRate,
  useDeleteFxRate,
  formatRate,
  type FxRate,
} from "@/api/fx-rates";

/**
 * Pricing-redesign: FxRate admin (manual entry of CAD conversion rates).
 *
 * The quote composer looks up the latest row for (fromCurrency, toCurrency)
 * with effectiveAt <= asOf. Without rows, non-CAD external carriers fail with
 * FX_RATE_MISSING. Append-only by convention; rates with bad values can be
 * edited (rate + source) or deleted.
 */

interface FormState {
  id?: string;
  fromCurrency: string;
  toCurrency: string;
  rateText: string;
  effectiveAt: string;
  source: string;
}

const todayIso = () => new Date().toISOString().split("T")[0];

const DEFAULT_STATE: FormState = {
  fromCurrency: "USD",
  toCurrency: "CAD",
  rateText: "1.36",
  effectiveAt: todayIso(),
  source: "manual",
};

function fromRow(row: FxRate): FormState {
  return {
    id: row.id,
    fromCurrency: row.fromCurrency,
    toCurrency: row.toCurrency,
    rateText: String(row.rate),
    effectiveAt: row.effectiveAt.slice(0, 10),
    source: row.source,
  };
}

export function FxRatesScreen() {
  const [editing, setEditing] = useState<FormState | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const { data: rates, isLoading, refetch } = useGetFxRates();

  const createMutation = useCreateFxRate({
    onSuccess: () => {
      toast.success("Created", { description: "FX rate created" });
      void refetch();
      setEditing(null);
    },
    onError: (e) => {
      toast.error("Create failed", { description: e.message });
    },
  });

  const updateMutation = useUpdateFxRate({
    onSuccess: () => {
      toast.success("Saved", { description: "FX rate updated" });
      void refetch();
      setEditing(null);
    },
    onError: (e) => {
      toast.error("Save failed", { description: e.message });
    },
  });

  const deleteMutation = useDeleteFxRate({
    onSuccess: () => {
      toast.success("Deleted", { description: "FX rate deleted" });
      void refetch();
      setDeleteId(null);
    },
    onError: (e) => {
      toast.error("Delete failed", { description: e.message });
    },
  });

  const handleSave = async () => {
    if (!editing) return;
    const rate = parseFloat(editing.rateText);
    if (Number.isNaN(rate) || rate <= 0) {
      toast.error("Validation", {
        description: "Rate must be a positive number.",
      });
      return;
    }
    if (editing.fromCurrency.length !== 3 || editing.toCurrency.length !== 3) {
      toast.error("Validation", {
        description: "Currency codes must be 3 letters (ISO 4217).",
      });
      return;
    }

    if (editing.id) {
      await updateMutation.mutateAsync({
        id: editing.id,
        data: { rate, source: editing.source },
      });
    } else {
      await createMutation.mutateAsync({
        fromCurrency: editing.fromCurrency.toUpperCase(),
        toCurrency: editing.toCurrency.toUpperCase(),
        rate,
        effectiveAt: editing.effectiveAt,
        source: editing.source,
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <DollarSign className="h-5 w-5" />
          FX Rates
        </h2>
        <Button size="sm" onClick={() => setEditing(DEFAULT_STATE)}>
          <Plus className="h-4 w-4 mr-1" />
          Add rate
        </Button>
      </div>

      <p className="text-sm text-muted-foreground">
        Manual conversion rates used at quote time when external carriers return
        non-CAD prices. Latest effective row for a (from, to) pair wins; older
        rows stay around for audit and historical snapshot lookups.
      </p>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">
            {rates?.length ?? 0} rate{rates?.length === 1 ? "" : "s"}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>From → To</TableHead>
                <TableHead>Rate</TableHead>
                <TableHead>Effective at</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(rates ?? []).length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    className="text-center py-6 text-sm text-muted-foreground"
                  >
                    No FX rates yet. Add one before quoting non-CAD external
                    carriers.
                  </TableCell>
                </TableRow>
              ) : (
                (rates ?? []).map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="text-sm font-medium">
                      {r.fromCurrency} → {r.toCurrency}
                    </TableCell>
                    <TableCell className="text-sm font-mono">
                      {formatRate(r.rate)}
                    </TableCell>
                    <TableCell className="text-sm">
                      {r.effectiveAt.slice(0, 10)}
                    </TableCell>
                    <TableCell className="text-sm">{r.source}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {r.createdAt.slice(0, 10)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 w-7 p-0"
                          onClick={() => setEditing(fromRow(r))}
                        >
                          <Edit className="h-3 w-3" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                          onClick={() => setDeleteId(r.id)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create / edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing?.id ? "Edit" : "Add"} FX rate</DialogTitle>
          </DialogHeader>

          {editing && (
            <div className="space-y-3 py-2">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">From currency</Label>
                  <Input
                    value={editing.fromCurrency}
                    onChange={(e) =>
                      setEditing({
                        ...editing,
                        fromCurrency: e.target.value.toUpperCase().slice(0, 3),
                      })
                    }
                    placeholder="USD"
                    disabled={!!editing.id}
                    maxLength={3}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">To currency</Label>
                  <Input
                    value={editing.toCurrency}
                    onChange={(e) =>
                      setEditing({
                        ...editing,
                        toCurrency: e.target.value.toUpperCase().slice(0, 3),
                      })
                    }
                    placeholder="CAD"
                    disabled={!!editing.id}
                    maxLength={3}
                  />
                </div>
              </div>
              {editing.id && (
                <p className="text-[10px] text-muted-foreground">
                  Currencies + effectiveAt are part of the row's identity.
                  Delete + create to change them.
                </p>
              )}
              <div className="space-y-1">
                <Label className="text-xs">Rate (1 from = X to)</Label>
                <Input
                  type="number"
                  step="0.0001"
                  min="0"
                  value={editing.rateText}
                  onChange={(e) =>
                    setEditing({ ...editing, rateText: e.target.value })
                  }
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Effective at</Label>
                <Input
                  type="date"
                  value={editing.effectiveAt}
                  onChange={(e) =>
                    setEditing({ ...editing, effectiveAt: e.target.value })
                  }
                  disabled={!!editing.id}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Source</Label>
                <Input
                  value={editing.source}
                  onChange={(e) =>
                    setEditing({ ...editing, source: e.target.value })
                  }
                  placeholder="manual / boc-noon / openexchange"
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isPending}>
              {editing?.id ? "Save" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog
        open={!!deleteId}
        onOpenChange={(o) => !o && setDeleteId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete FX rate</AlertDialogTitle>
            <AlertDialogDescription>
              Permanently removes the row. Note: shipments quoted earlier may
              have snapshotted this rate; deletion doesn't touch their stored
              prices.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (deleteId) deleteMutation.mutate(deleteId);
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
